import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  limit,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { TradingRules, TradingRulesVersion } from '../types';
import { defaultGlobalRules } from '../config/defaultTradingRules';

export class TradingRulesService {
  private static RULES_COLLECTION = 'trading_rules';

  /**
   * Initialize default global rules if they don't exist
   */
  static async initializeDefaultRules(): Promise<void> {
    try {
      // Check if global rules exist
      const globalRules = await this.getGlobalRules();
      
      if (!globalRules) {
        // Create default global rules
        const docRef = doc(collection(db, this.RULES_COLLECTION));
        await setDoc(docRef, {
          ...defaultGlobalRules,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        console.log('Default global rules initialized');
      }
    } catch (error) {
      console.error('Error initializing default rules:', error);
      throw error;
    }
  }

  /**
   * Get global trading rules
   */
  static async getGlobalRules(): Promise<TradingRules | null> {
    // Global rules don't need authentication check
    const q = query(
      collection(db, this.RULES_COLLECTION),
      where('isGlobal', '==', true),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const docSnap = querySnapshot.docs[0];
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: data.createdAt?.toDate(),
      updatedAt: data.updatedAt?.toDate(),
      activatedAt: data.activatedAt?.toDate(),
      // Ensure minEntryScore exists with default value
      minEntryScore: data.minEntryScore ?? 60,
      // Lazy migration: treat unversioned docs as v1
      version: data.version ?? 1,
      status: data.status ?? 'active',
    } as TradingRules;
  }

  /**
   * Get portfolio-specific rules or fall back to global
   */
  static async getPortfolioRules(portfolioId: string): Promise<TradingRules> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // First try to get portfolio-specific rules, checking for legacy data too
    const q = query(
      collection(db, this.RULES_COLLECTION),
      where('portfolioId', '==', portfolioId),
      where('isGlobal', '==', false),
      limit(1)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const docSnap = querySnapshot.docs[0];
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        activatedAt: data.activatedAt?.toDate(),
        // Ensure minEntryScore exists with default value
        minEntryScore: data.minEntryScore ?? 60,
        // Lazy migration: treat unversioned docs as v1
        version: data.version ?? 1,
        status: data.status ?? 'active',
      } as TradingRules;
    }
    
    // Fall back to global rules
    const globalRules = await this.getGlobalRules();
    if (!globalRules) {
      throw new Error('No global rules found. Please initialize default rules.');
    }
    
    return globalRules;
  }

  /**
   * Create portfolio-specific rules
   */
  static async createPortfolioRules(
    portfolioId: string,
    overrides: Partial<TradingRules>
  ): Promise<TradingRules> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    
    // Get global rules as base
    const globalRules = await this.getGlobalRules();
    if (!globalRules) {
      throw new Error('No global rules found');
    }
    
    // Create new rules document
    const docRef = doc(collection(db, this.RULES_COLLECTION));
    const newRules = {
      ...globalRules,
      ...overrides,
      id: docRef.id,
      userId: user.uid,
      portfolioId,
      isGlobal: false,
      name: overrides.name || `${portfolioId} Rules`,
      version: 1,
      status: 'active',
      activatedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Remove fields that shouldn't be copied
    delete (newRules as any).id;

    await setDoc(docRef, newRules);

    // Create initial v1 in versions subcollection
    const versionRef = doc(collection(db, this.RULES_COLLECTION, docRef.id, 'versions'));
    await setDoc(versionRef, {
      rulesId: docRef.id,
      version: 1,
      status: 'active',
      rules: this.extractRuleValues(newRules as any),
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      activatedAt: serverTimestamp(),
    });

    return {
      ...newRules,
      id: docRef.id,
      createdAt: new Date(),
      updatedAt: new Date(),
      activatedAt: new Date(),
    } as TradingRules;
  }

  /**
   * Update trading rules
   */
  static async updateRules(
    rulesId: string,
    updates: Partial<TradingRules>
  ): Promise<void> {
    const docRef = doc(db, this.RULES_COLLECTION, rulesId);
    
    // Remove fields that shouldn't be updated
    delete updates.id;
    delete updates.createdAt;
    
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  }

  /**
   * Get all rules (for management UI)
   */
  static async getAllRules(): Promise<TradingRules[]> {
    const q = query(
      collection(db, this.RULES_COLLECTION),
      orderBy('isGlobal', 'desc'),
      orderBy('name')
    );
    
    const querySnapshot = await getDocs(q);
    const rules: TradingRules[] = [];
    
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      rules.push({
        ...data,
        id: docSnap.id,
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
        activatedAt: data.activatedAt?.toDate(),
        version: data.version ?? 1,
        status: data.status ?? 'active',
      } as TradingRules);
    });
    
    return rules;
  }

  /**
   * Calculate effective rules for a portfolio
   * (Merges global with portfolio-specific overrides)
   */
  static async getEffectiveRules(portfolioId: string): Promise<TradingRules> {
    const portfolioRules = await this.getPortfolioRules(portfolioId);
    
    // If it's already portfolio-specific, return as-is
    if (!portfolioRules.isGlobal) {
      return portfolioRules;
    }
    
    // Otherwise it's global rules (no portfolio override exists)
    return portfolioRules;
  }

  /**
   * Delete portfolio-specific rules (revert to global)
   */
  static async deletePortfolioRules(rulesId: string): Promise<void> {
    // Verify it's not global rules
    const docRef = doc(db, this.RULES_COLLECTION, rulesId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists() && docSnap.data().isGlobal) {
      throw new Error('Cannot delete global rules');
    }

    await updateDoc(docRef, {
      isActive: false,
      updatedAt: serverTimestamp()
    });
  }

  // ========== Version Control Methods ==========

  /**
   * Get the current draft for a rules document
   */
  static async getDraft(rulesId: string): Promise<TradingRulesVersion | null> {
    const q = query(
      collection(db, this.RULES_COLLECTION, rulesId, 'versions'),
      where('status', '==', 'draft'),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];
    const data = docSnap.data();
    return this.convertVersionFromFirestore(docSnap.id, data);
  }

  /**
   * Create a draft by copying current active rule values
   */
  static async createDraft(rulesId: string): Promise<TradingRulesVersion> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Get current active rules
    const rulesRef = doc(db, this.RULES_COLLECTION, rulesId);
    const rulesSnap = await getDoc(rulesRef);
    if (!rulesSnap.exists()) throw new Error('Rules not found');

    const currentRules = rulesSnap.data();
    const currentVersion = currentRules.version ?? 1;

    // Lazy migration: if no version field, set v1 on main doc and create v1 in subcollection
    if (!currentRules.version) {
      await updateDoc(rulesRef, {
        version: 1,
        status: 'active',
        activatedAt: currentRules.updatedAt || serverTimestamp(),
      });

      // Create v1 archive entry
      const v1Ref = doc(collection(db, this.RULES_COLLECTION, rulesId, 'versions'));
      await setDoc(v1Ref, {
        rulesId,
        version: 1,
        status: 'active',
        rules: this.extractRuleValues(currentRules),
        createdAt: currentRules.createdAt || serverTimestamp(),
        createdBy: currentRules.userId || user.uid,
        activatedAt: currentRules.updatedAt || serverTimestamp(),
      });
    }

    // Check if draft already exists
    const existingDraft = await this.getDraft(rulesId);
    if (existingDraft) return existingDraft;

    // Create draft version doc
    const draftRef = doc(collection(db, this.RULES_COLLECTION, rulesId, 'versions'));
    const draftVersion = currentVersion + 1;

    const draftData = {
      rulesId,
      version: draftVersion,
      status: 'draft',
      rules: this.extractRuleValues(currentRules),
      createdAt: serverTimestamp(),
      createdBy: user.uid,
    };

    await setDoc(draftRef, draftData);

    return {
      id: draftRef.id,
      rulesId,
      version: draftVersion,
      status: 'draft',
      rules: this.extractRuleValues(currentRules) as Partial<TradingRules>,
      createdAt: new Date(),
      createdBy: user.uid,
    };
  }

  /**
   * Update draft rule values
   */
  static async updateDraft(
    rulesId: string,
    draftId: string,
    ruleUpdates: Partial<TradingRules>
  ): Promise<void> {
    const draftRef = doc(db, this.RULES_COLLECTION, rulesId, 'versions', draftId);
    await updateDoc(draftRef, {
      rules: this.extractRuleValues(ruleUpdates),
    });
  }

  /**
   * Activate a draft: promote draft values to the main doc, archive previous active
   */
  static async activateDraft(
    rulesId: string,
    draftId: string,
    changeNotes?: string
  ): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Get the draft
    const draftRef = doc(db, this.RULES_COLLECTION, rulesId, 'versions', draftId);
    const draftSnap = await getDoc(draftRef);
    if (!draftSnap.exists()) throw new Error('Draft not found');

    const draftData = draftSnap.data();
    if (draftData.status !== 'draft') throw new Error('Version is not a draft');

    const draftVersion = draftData.version;
    const draftRules = draftData.rules;

    // Get current active doc
    const rulesRef = doc(db, this.RULES_COLLECTION, rulesId);
    const rulesSnap = await getDoc(rulesRef);
    if (!rulesSnap.exists()) throw new Error('Rules not found');

    const currentRules = rulesSnap.data();

    const batch = writeBatch(db);

    // 1. Find the current active version in subcollection and archive it
    const activeVersionQuery = query(
      collection(db, this.RULES_COLLECTION, rulesId, 'versions'),
      where('status', '==', 'active'),
      limit(1)
    );
    const activeVersionSnap = await getDocs(activeVersionQuery);
    if (!activeVersionSnap.empty) {
      const activeVersionRef = activeVersionSnap.docs[0].ref;
      batch.update(activeVersionRef, {
        status: 'archived',
        archivedAt: serverTimestamp(),
      });
    }

    // 2. Copy draft rule values to main doc
    batch.update(rulesRef, {
      ...draftRules,
      version: draftVersion,
      status: 'active',
      activatedAt: serverTimestamp(),
      changeNotes: changeNotes || null,
      updatedAt: serverTimestamp(),
    });

    // 3. Update draft doc to 'active'
    batch.update(draftRef, {
      status: 'active',
      activatedAt: serverTimestamp(),
      changeNotes: changeNotes || null,
    });

    await batch.commit();
  }

  /**
   * Discard (delete) a draft version
   */
  static async discardDraft(rulesId: string, draftId: string): Promise<void> {
    const draftRef = doc(db, this.RULES_COLLECTION, rulesId, 'versions', draftId);
    const draftSnap = await getDoc(draftRef);
    if (draftSnap.exists() && draftSnap.data().status !== 'draft') {
      throw new Error('Can only discard draft versions');
    }
    await deleteDoc(draftRef);
  }

  /**
   * Get version history for a rules document
   */
  static async getVersionHistory(rulesId: string): Promise<TradingRulesVersion[]> {
    const q = query(
      collection(db, this.RULES_COLLECTION, rulesId, 'versions'),
      orderBy('version', 'desc')
    );

    const snapshot = await getDocs(q);
    const versions: TradingRulesVersion[] = [];

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      versions.push(this.convertVersionFromFirestore(docSnap.id, data));
    });

    return versions;
  }

  /**
   * Get effective rules along with version metadata for recommendation stamping
   */
  static async getEffectiveRulesWithVersion(
    portfolioId: string
  ): Promise<{ rules: TradingRules; rulesId: string; version: number }> {
    const rules = await this.getEffectiveRules(portfolioId);
    return {
      rules,
      rulesId: rules.id,
      version: rules.version ?? 1,
    };
  }

  // ========== Private Helpers ==========

  /**
   * Extract only the rule value fields (no metadata) for storing in version snapshots
   */
  private static extractRuleValues(data: any): Record<string, any> {
    const {
      id, userId, createdAt, updatedAt, activatedAt, archivedAt,
      version, status, changeNotes,
      ...ruleValues
    } = data;
    return ruleValues;
  }

  /**
   * Convert a Firestore version doc to TradingRulesVersion
   */
  private static convertVersionFromFirestore(id: string, data: any): TradingRulesVersion {
    return {
      id,
      rulesId: data.rulesId,
      version: data.version,
      status: data.status,
      rules: data.rules || {},
      createdAt: data.createdAt?.toDate() || new Date(),
      createdBy: data.createdBy || '',
      activatedAt: data.activatedAt?.toDate(),
      archivedAt: data.archivedAt?.toDate(),
      changeNotes: data.changeNotes,
    };
  }
}